import { PerformaSessionState } from "app/appModels";
import { CorePermissions, PermissionExplorer } from "app/appPermissions";
import { AccountAccessType, AppSettingsResponse, UserType } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useReferredState } from "hooks/useReferredState";
import i18next from "i18next";
import { jwtDecode } from "jwt-decode";
import LibUtils from "lib/LibUtils";
import React, { useEffect, useRef, useState } from "react";
import { AnonymousUser, AuthJwt, AuthJwtDecoded, AuthRefreshUserProps, AuthService, AuthUser } from "services/authService";
import { reloadloadI18nConfig } from "services/i18n";
import { SessionService } from "services/sessionService";
import { SessionStore } from "services/SessionStore";

///////////////////////////////////////////////////////////////////////////////

type SessionContextType = {
    config: AppSettingsResponse;
    appBuild: AppBuild | null;

    isAuthenticated: boolean;
    isTwoFactorPassed: boolean;
    user: AuthUser;

    /**
     * Atualiza propriedades do usuário.
     * @param u
     */
    refreshUserProperties: (newProps: AuthRefreshUserProps) => void;

    /**
     * Cria a sessão do usuário.
     * @param jwt
     */
    createSession: (jwt: AuthJwt) => void;

    /**
     * Finaliza a sessão do usuário: realiza Logout e limpa cache de carrossel.
     * @param callback
     */
    finalizeSession: (callback?: VoidFunction) => void;

    /**
     * Finaliza a sessão do usuário: realiza Logout e limpa cache de carrossel.
     * @param callback
     */
    clearSession: (callback?: VoidFunction) => void;

    /**
     * Reinicia os dados internos da sessão do usuário.
     */
    resetSession: () => void;

    /**
     * Confirma que o usuário realizou 2FA com sucesso.
     */
    confirmTwoFactorAuthentication: () => void;

    uiCulture: string;
    canCallIaRequests: boolean;

    /**
     * Obtém o nome do idioma preferido do usuário.
     * @param locale
     * @returns
     */
    getLocaleName(locale: string): string;

    /**
     * Troca o idioma da interface.
     * @param culture
     */
    changeUiCulture: (culture: string) => void;

    /**
     * Troca o tema da interface.
     * @param themeName
     */
    changeTheme: (theme: string) => void;

    permissionExplorer: PermissionExplorer;
};

export const SessionContext = React.createContext<SessionContextType>(undefined!);

export const useSession = () => React.useContext(SessionContext);

/////////////////////////////////////////////////////////////////////////

interface Props {
    initialLocale: string;
    children?: React.ReactNode;
    splashScreen: React.FC<{ mode: PerformaSessionState; lastError: any; }>;
}

/**
 * Fornece o Contexto de Sessão de Usuário.
 * @param param0
 */
export const SessionProvider: React.FC<Props> = (props) => {

    // Recupera usuário salvo na sessão do navegador.
    const loadedUser = useRef<AuthUser>(SessionStore.readUser());

    //
    const { children, initialLocale, splashScreen: SplashScreen } = props;
    const [settings, setSettings] = useState<AppSettingsResponse | null>(null);
    const [mode, setMode] = useState<PerformaSessionState>("init");
    const [lastError, setLastError] = useState<any>(null);
    const [uiCulture, setUiCulture] = useState<string>(initialLocale);
    const [contextBuilt, setContextBuilt] = useState<boolean>(false);
    const [user, setUser, refUser] = useReferredState<AuthUser>(loadedUser.current);
    const [contextValue, setContextValue] = useState<SessionContextType>(undefined!);
    const [appBuild, setAppBuild] = useState<AppBuild | null>(null);
    const lastLogout = useRef<Date | null>(null);

    /**
     * Atualiza as propriedades do usuário e INVOCA nova construção de contexto.
     * @param newUser
     */
    const internalUserUpdate = (newUser: AuthUser) => {
        setUiCulture(newUser.locale);
        setUser(newUser);
        setContextBuilt(false);
        SessionStore.storeUser(newUser);
    };

    /**
     * Atualiza o Idioma da Interface e INVOCA nova construção de contexto.
     * @param culture
     * @returns
     */
    const changeI18nCulture = (culture: string) => {
        return new Promise<void>((resolve) => {
            if (culture != i18next.language) {
                i18next.changeLanguage(culture, () => {
                    window.uiLanguage = culture;
                    resolve();
                });
            }
            else
                resolve();
        });
    };

    /**
     * Confirma que o usuário realizou 2FA com sucesso.
     */
    const confirmTwoFactorAuthentication = () => {
        const newUser: AuthUser = { ...refUser.current, twoFactorPassed: true };
        internalUserUpdate(newUser);
    };

    /**
     * Cria a sessão do usuário.
     * @param jwt
     */
    const createSession = (jwt: AuthJwt) => {
        const jd = jwtDecode(jwt.access_token) as AuthJwtDecoded;
        const typedAccessType = jd.access_type as keyof typeof AccountAccessType;
        const userType = jd.userType as keyof typeof UserType;
        const permissions = jwt.permissions ? jwt.permissions.split(',') : null;

        const user: AuthUser = {
            accessType: AccountAccessType[typedAccessType],
            userType: UserType[userType],
            userId: jd.kid,
            name: jd.name,
            email: jd.email,
            locale: jd.locale,
            keepConnected: jd.keep_connected === "true",
            signedAggrement: jd.signed_aggrement === "true",
            spyUserId: jd.spyUserId,
            spyUsername: jd.spyUsername,
            thumbnailUrl: jwt.thumbnailUrl,
            permissions: permissions!,
            continueAuthStamp: jwt.continueAuthStamp ?? undefined,
            lastUpdate: 0,
            twoFactorPassed: jwt.bypassTwoFactor === "true"
        };

        SessionService.current.clearCarouselData();
        SessionStore.storeJWT(jwt, false);
        SessionStore.storeUser(user);
        setUser(user);
        setUiCulture(user.locale);
        setContextBuilt(false);
    };

    /**
     * Finaliza a sessão do usuário: realiza Logout e limpa cache de carrossel.
     * @param callback
     */
    const finalizeSession = (callback?: VoidFunction) => {
        SessionService.current.clearCarouselData();

        AuthService
            .accountLogout()
            .catch((e) => console.error(e))
            .finally(() => clearSession(callback));
    };

    /**
     * Limpa os dados da sessão do usuário (logout).
     * @param callback
     */
    const clearSession = (callback?: VoidFunction) => {
        AuthService.logout()
            .catch((e) => console.error(e))
            .finally(() => {
                lastLogout.current = new Date();
                resetSession();
                if (callback) callback();
            });
    };

    /**
     * Reset: reinicia os dados internos da sessão do usuário.
     * @param callback
     */
    const resetSession = () => {
        SessionStore.clearJWT();
        SessionStore.storeUser(AnonymousUser);
        SessionStore.clearCountryCode();
        setUser(AnonymousUser);
        setUiCulture(initialLocale);
    };

    /**
     * Atualiza propriedades do usuário.
     * @param u
     */
    const refreshUserProperties = (u: AuthRefreshUserProps) => {
        const newUser: AuthUser = { ...user, ...u };
        internalUserUpdate(newUser);
    };

    /**
     * Troca o idioma da interface.
     * @param culture
     */
    const changeUiCulture = (culture: string) => {
        changeI18nCulture(culture)
            .then(() => {
                window.uiLanguage = culture;
                setUiCulture(culture);
                setContextBuilt(false);
            });
    };

    /**
     * Troca o tema da interface.
     * @param themeName
     */
    const changeTheme = (themeName: string) => {
        setSettings(state => ({ ...state!, tenantUIConfig: { ...state!.tenantUIConfig, themeName } }));
        setContextBuilt(false);
    };

    /**
     * Obtém o nome do idioma preferido do usuário.
     * @param locale
     * @returns
     */
    const getLocaleName = (locale: string): string => {
        var localeDef = contextValue.config.inputLocales.find(x => x.locale == locale);
        if (localeDef) return localeDef.nameR.find(x => x.locale == contextValue.uiCulture)?.text ?? locale;
        return locale;
    };

    /**
     * Carrega a Configuração do Sistema.
     */
    async function loadSettings() {
        try {
            if (user.userId != "") {
                await changeI18nCulture(user.locale);
                setUiCulture(user.locale);
            }

            const previousSettings = LibUtils.deepCopy(settings);

            const currentSettings = await SessionService.getSettings();
            window.document.title = currentSettings.tenantUIConfig.title;
            window.timezones = currentSettings.timezones;

            if (!LibUtils.deepCompare(previousSettings, currentSettings)) {
                setSettings(currentSettings);
                setContextBuilt(false);
                reloadloadI18nConfig(SessionService.current.apiPaths.coreApi);
            }
        }
        catch (error: any) {
            setMode("error");
            setLastError(error);
        }
    }

    /**
     * Ao carregar pela primeira vez ou quando uma nova sessão for criada.
     */
    useEffect(() => {
        // Carrega a versão do APP.
        async function loadBuildNumber() {
            try {
                const response = await fetch("/build-number.json");
                if (response.status == 200) {
                    const json = await response.json();
                    return (json as AppBuild);
                }
            }
            catch {
                console.error("Cannot get PowerMinds's build info");
            }
            return null;
        }

        async function init() {
            var build = await loadBuildNumber();
            if (build != null && SessionService.current.checkNewSystemVersion(build.buildVersion)) {
                setMode("newRelease");
                setTimeout(() => {
                    window.location.reload();
                    //Mudar para o código abaixo se não funcionar.
                    //window.location.href = window.location.href,
                }, 2000);
            }
            else {
                setAppBuild(build);
                loadSettings();
            }
        }

        init();
    }, [lastLogout.current]);

    /**
     * Ao solicitar Novo Contexto.
     */
    useDidUpdateEffect(() => {
        if (!contextBuilt && settings) {

            window.uiLanguage = uiCulture;

            setContextValue({
                config: settings!,
                appBuild: appBuild,

                user,
                isAuthenticated: user.userId !== "",
                isTwoFactorPassed: user.twoFactorPassed,

                refreshUserProperties,

                createSession,
                finalizeSession,
                clearSession,
                resetSession,
                confirmTwoFactorAuthentication,

                canCallIaRequests: user.permissions.includes(CorePermissions.UserCallIaRequests),
                uiCulture,
                getLocaleName,
                changeUiCulture,
                changeTheme,

                permissionExplorer: new PermissionExplorer(user.permissions, user.userType)
            });

            changeI18nCulture(uiCulture).then(() => {
                setMode("ready");
                setContextBuilt(true);
            });
        }
    }, [contextBuilt, settings]);

    /**
     * Quando as Permissões do usuário forem alteradas.
     */
    useEffect(() => {
        function handleNewJwt(data: CustomEventInit<string>) {
            if (data.detail === "changed") {
                const token = SessionStore.readToken();
                if (token != null) {
                    const permissions = token.permissions.split(',') ?? [];

                    const areEqual = LibUtils.deepCompare(permissions, user.permissions);
                    if (!areEqual) {
                        const newUser = { ...user, permissions };
                        internalUserUpdate(newUser);
                    }
                }
            }
        }

        document.addEventListener("storeJWT", handleNewJwt);
        return () => {
            document.removeEventListener("storeJWT", handleNewJwt);
        };
    }, [user, contextBuilt]);

    ///////////////////////////////////////////////////////////////

    return mode != "ready"
        ? <SplashScreen mode={mode} lastError={lastError} />
        : (
            <SessionContext.Provider value={contextValue}>
                {contextValue && children}
            </SessionContext.Provider>
        );
};
